// frontend/src/Components/Tasks/CreateTaskModal.jsx
//
// Manual task creation modal. Triggered from BootNav by the
// fa-clipboard-list-check button (gated by userCanUseTasks()).
//
// Scope:
//   - description (with @mentions)
//   - assignee dropdown
//   - up to 5 image attachments (optional). Camera or file picker — same
//     input on mobile (capture="environment" opens the rear camera if
//     present, otherwise the file picker).
//
// Submit flow:
//   1. Validate locally (description + assignee).
//   2. POST /api/tasks → returns the created task.
//   3. If any attachments were queued, POST them in one multipart request.
//      Failure here doesn't roll back the task — the user can retry uploads
//      from TaskDetailModal in a future iteration; for now we surface a
//      partial-success message and close.
//   4. Invoke onCreated(task) so the parent can refresh.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { jwtDecode } from 'jwt-decode';
import { createTask, listPracticeUsers, uploadTaskAttachments } from '../../api';
import MentionTextarea from './MentionTextarea';

// JWT-derived current user ID — used to pre-select self in the assignee
// dropdown so creating a task for yourself is a one-click flow.
function readCurrentUserId() {
  try {
    const token = sessionStorage.getItem('user');
    if (!token) return null;
    const decoded = jwtDecode(token);
    return decoded?._id || decoded?.id || null;
  } catch {
    return null;
  }
}

const MAX_ATTACHMENTS = 5;
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];

export default function CreateTaskModal({ show, onClose, onCreated }) {
  const fileInputRef = useRef(null);
  // Tracks the previous "first @mention" so we only auto-flip the assignee
  // when the first mention actually changes — not on every keystroke that
  // happens to leave the same first mention in place. Also lets the manual
  // assignee dropdown stick once the user has picked a different colleague.
  const prevFirstMentionRef = useRef(null);
  const currentUserId = useMemo(() => readCurrentUserId(), []);
  const [practiceUsers, setPracticeUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [description, setDescription] = useState('');
  const [mentionedUserIds, setMentionedUserIds] = useState([]);
  const [assigneeId, setAssigneeId] = useState('');
  // Each entry: { file: File, previewUrl: string (objectURL) }
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [resetSignal, setResetSignal] = useState(0);

  // Load practice users (filtered server-side to features.tasks: true) when
  // the modal opens. Re-fetch each open so a newly enabled colleague shows
  // up without a page refresh.
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoadingUsers(true);
    listPracticeUsers()
      .then((users) => { if (!cancelled) setPracticeUsers(Array.isArray(users) ? users : []); })
      .finally(() => { if (!cancelled) setLoadingUsers(false); });
    return () => { cancelled = true; };
  }, [show]);

  // Reset form on each fresh open. Pre-select self as the default assignee —
  // self-assignment is the most common path for personal todos. User can
  // pick someone else from the dropdown.
  useEffect(() => {
    if (show) {
      setDescription('');
      setMentionedUserIds([]);
      setAssigneeId(currentUserId || '');
      prevFirstMentionRef.current = null;
      setPendingAttachments((prev) => {
        // Free old object URLs before clearing state.
        prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
        return [];
      });
      setError(null);
      setWarning(null);
      setResetSignal((n) => n + 1);
    }
  }, [show, currentUserId]);

  // Revoke any remaining object URLs on unmount.
  useEffect(() => () => {
    pendingAttachments.forEach((p) => URL.revokeObjectURL(p.previewUrl));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sort: self first (so it's at the top of the dropdown next to the
  // pre-selected default), then the rest alphabetical.
  const sortedUsers = useMemo(() => {
    return [...practiceUsers].sort((a, b) => {
      if (currentUserId) {
        if (String(a._id) === String(currentUserId)) return -1;
        if (String(b._id) === String(currentUserId)) return 1;
      }
      const aName = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
      const bName = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [practiceUsers, currentUserId]);

  const canSubmit = description.trim().length > 0 && assigneeId && !submitting;

  const handleMentionChange = ({ text, mentionedUserIds: ids }) => {
    setDescription(text);
    const safeIds = ids || [];
    setMentionedUserIds(safeIds);

    // Auto-assign to the first mentioned colleague — but only when the
    // identity of that first mention actually changes. This way:
    //   • typing "@Bob …" flips the assignee from self → Bob
    //   • adding a second @Alice doesn't override (first wins)
    //   • removing Bob so Alice becomes first → assignee = Alice
    //   • clearing all mentions leaves the current assignee untouched
    const firstId = safeIds[0] || null;
    if (firstId && firstId !== prevFirstMentionRef.current) {
      setAssigneeId(firstId);
    }
    prevFirstMentionRef.current = firstId;
  };

  const handleFileSelect = (e) => {
    const incoming = Array.from(e.target.files || []);
    // Allow re-picking the same file by clearing the input value after read.
    e.target.value = '';
    if (!incoming.length) return;

    const accepted = [];
    const rejected = [];
    for (const f of incoming) {
      if (!ALLOWED_MIME.includes(f.type)) {
        rejected.push(`${f.name}: unsupported format`);
        continue;
      }
      accepted.push(f);
    }

    setPendingAttachments((prev) => {
      const remaining = MAX_ATTACHMENTS - prev.length;
      const trimmed = accepted.slice(0, Math.max(0, remaining));
      const overflow = accepted.length - trimmed.length;
      const next = [
        ...prev,
        ...trimmed.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f) })),
      ];
      const messages = [...rejected];
      if (overflow > 0) {
        messages.push(`${overflow} image(s) skipped — max ${MAX_ATTACHMENTS} per task.`);
      }
      setWarning(messages.length ? messages.join(' ') : null);
      return next;
    });
  };

  const handleRemoveAttachment = (idx) => {
    setPendingAttachments((prev) => {
      const next = [...prev];
      const removed = next.splice(idx, 1)[0];
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setWarning(null);
    try {
      const result = await createTask({
        description: description.trim(),
        assignedToUserId: assigneeId,
        mentionedUserIds,
      });
      const created = result?.task || null;

      // Upload attachments after the task exists. Surface upload failure
      // separately — the task itself is already saved.
      if (created?._id && pendingAttachments.length) {
        try {
          await uploadTaskAttachments(
            created._id,
            pendingAttachments.map((p) => p.file),
          );
        } catch (uploadErr) {
          const msg = uploadErr?.response?.data?.error || uploadErr?.message || 'Attachment upload failed.';
          // Task is still created; user is informed but modal closes so
          // they can review the task and retry from the detail view.
          setWarning(`Task created, but ${msg}`);
        }
      }

      onCreated?.(created);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Could not create task.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" size="lg">
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          <i className="fa-light fa-clipboard-list-check me-2" />
          New Task
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
        {warning && <Alert variant="warning" className="py-2 small">{warning}</Alert>}

        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold">
            Description <span className="text-danger">*</span>
          </Form.Label>
          <MentionTextarea
            value={description}
            onChange={handleMentionChange}
            practiceUsers={practiceUsers}
            placeholder="What needs doing? Use @ to mention a colleague."
            rows={5}
            disabled={submitting}
            resetSignal={resetSignal}
            // Lock at ~5 rows of visible height; if the user types more,
            // the textarea scrolls internally rather than pushing the rest
            // of the modal down. Manual resize stays available.
            textareaStyle={{ minHeight: '9rem', maxHeight: '50vh', overflowY: 'auto', resize: 'vertical' }}
          />
          <Form.Text muted>
            Type @ to tag someone — they'll get a notification.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold">
            Assign to <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            disabled={submitting || loadingUsers}
          >
            <option value="">{loadingUsers ? 'Loading colleagues…' : 'Select a colleague…'}</option>
            {sortedUsers.map((u) => {
              const isMe = currentUserId && String(u._id) === String(currentUserId);
              const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
              return (
                <option key={u._id} value={u._id}>
                  {isMe ? `${name} (You)` : name}
                </option>
              );
            })}
          </Form.Select>
          {!loadingUsers && sortedUsers.length === 0 && (
            <Form.Text className="text-warning">
              No colleagues with the Tasks feature enabled. Ask a SystemAdmin to enable it.
            </Form.Text>
          )}
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold mb-1 d-block">
            Attachments
            <span className="text-muted fw-normal ms-2">
              {pendingAttachments.length}/{MAX_ATTACHMENTS}
            </span>
          </Form.Label>

          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_MIME.join(',')}
            capture="environment"
            multiple
            className="d-none"
            onChange={handleFileSelect}
            disabled={submitting || pendingAttachments.length >= MAX_ATTACHMENTS}
          />
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={submitting || pendingAttachments.length >= MAX_ATTACHMENTS}
          >
            <i className="fa-light fa-camera me-2" />
            Add image / take photo
          </Button>

          {pendingAttachments.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-2">
              {pendingAttachments.map((p, idx) => (
                <div
                  key={p.previewUrl}
                  className="position-relative"
                  style={{ width: 80, height: 80 }}
                >
                  <img
                    src={p.previewUrl}
                    alt={p.file.name}
                    className="rounded border"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-danger position-absolute"
                    style={{
                      top: -6, right: -6,
                      width: 22, height: 22,
                      padding: 0,
                      borderRadius: '50%',
                      lineHeight: 1,
                      fontSize: '0.7rem',
                    }}
                    onClick={() => handleRemoveAttachment(idx)}
                    disabled={submitting}
                    aria-label={`Remove ${p.file.name}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <Form.Text muted className="d-block mt-2">
            Images are compressed automatically before upload. JPEG, PNG, or WebP.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting && <Spinner animation="border" size="sm" className="me-2" />}
          Create Task
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
